import React from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { Flame, Plus, Droplet, Dumbbell, Bell } from 'lucide-react';
import CalorieRing from '../components/CalorieRing';
import MacroBar from '../components/MacroBar';

const Dashboard = () => {
  const { user, goals, totals, exerciseTotal, waterCups, setWaterCups } = useApp();
  const navigate = useNavigate();

  const remaining = goals.calories - totals.calories + exerciseTotal;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <header className="bg-[#0066ee] text-white px-5 pt-6 pb-8 rounded-b-3xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-blue-100">Today</p>
            <h1 className="text-2xl font-bold mt-1">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-2 rounded-full hover:bg-white/10" aria-label="Notifications">
              <Bell size={22} />
            </button>
            <button onClick={() => navigate('/profile')} aria-label="Profile">
              <img src={user.avatar} alt={user.name} className="w-10 h-10 rounded-full border-2 border-white object-cover" />
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full w-fit">
          <Flame size={14} className="text-orange-300" />
          <span className="text-xs font-semibold">{user.streak}-day streak</span>
        </div>
      </header>

      {/* Calorie Card */}
      <section className="px-5 -mt-4 slide-up">
        <div className="bg-white rounded-2xl shadow-md p-5">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-bold text-gray-900">Calories</h2>
            <span className="text-xs text-gray-500">Remaining = Goal − Food + Exercise</span>
          </div>
          <div className="flex items-center gap-5 mt-2">
            <CalorieRing consumed={totals.calories} goal={goals.calories} exercise={exerciseTotal} />
            <div className="flex-1 space-y-2">
              <Stat label="Base Goal" value={goals.calories.toLocaleString()} dotClass="bg-gray-400" />
              <Stat label="Food" value={totals.calories.toLocaleString()} dotClass="bg-[#0066ee]" />
              <Stat label="Exercise" value={exerciseTotal.toLocaleString()} dotClass="bg-emerald-500" />
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500">Remaining</p>
                <p className={`text-xl font-bold ${remaining < 0 ? 'text-red-500' : 'text-gray-900'}`}>
                  {remaining.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Macros */}
      <section className="px-5 mt-4 slide-up">
        <div className="bg-white rounded-2xl shadow-md p-5">
          <h2 className="text-base font-bold text-gray-900 mb-4">Macros</h2>
          <div className="grid grid-cols-3 gap-4">
            <MacroBar label="Carbs" value={totals.carbs} goal={goals.carbs} color="#f59e0b" />
            <MacroBar label="Fat" value={totals.fat} goal={goals.fat} color="#a855f7" />
            <MacroBar label="Protein" value={totals.protein} goal={goals.protein} color="#10b981" />
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section className="px-5 mt-4 grid grid-cols-2 gap-3 slide-up">
        <QuickAction
          icon={<Plus size={20} />}
          title="Log Food"
          subtitle="Add to diary"
          onClick={() => navigate('/add-food/Snacks')}
          accent="bg-blue-50 text-[#0066ee]"
        />
        <QuickAction
          icon={<Dumbbell size={20} />}
          title="Add Exercise"
          subtitle={exerciseTotal ? `${exerciseTotal} cal burned` : 'Track workout'}
          onClick={() => navigate('/add-exercise')}
          accent="bg-emerald-50 text-emerald-600"
        />
      </section>

      {/* Water */}
      <section className="px-5 mt-4 mb-6 slide-up">
        <div className="bg-white rounded-2xl shadow-md p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Droplet size={18} className="text-sky-500" />
              <h2 className="text-base font-bold text-gray-900">Water</h2>
            </div>
            <span className="text-sm text-gray-600 font-medium">{waterCups} / {goals.water} cups</span>
          </div>
          <div className="flex gap-2">
            {Array.from({ length: goals.water }).map((_, i) => (
              <button
                key={i}
                onClick={() => setWaterCups(i < waterCups ? i : i + 1)}
                className={`flex-1 h-10 rounded-lg press-scale border-2 ${
                  i < waterCups ? 'bg-sky-500 border-sky-500' : 'bg-sky-50 border-sky-100'
                }`}
                aria-label={`Cup ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const Stat = ({ label, value, dotClass }) => (
  <div className="flex items-center gap-2">
    <span className={`w-2 h-2 rounded-full ${dotClass}`} />
    <span className="text-xs text-gray-500 flex-1">{label}</span>
    <span className="text-sm font-semibold text-gray-900">{value}</span>
  </div>
);

const QuickAction = ({ icon, title, subtitle, onClick, accent }) => (
  <button
    onClick={onClick}
    className="bg-white rounded-2xl shadow-md p-4 flex items-center gap-3 press-scale text-left"
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>{icon}</div>
    <div className="flex-1">
      <p className="text-sm font-bold text-gray-900">{title}</p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </div>
  </button>
);

export default Dashboard;
